import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress, TextField, Alert } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { createProductRequest, getProductRequestById, updateProductRequest } from "../../api/productRequests/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getProductList, getProductById } from "../../api/products";
import { api } from "../../api";
import dayjs from "dayjs";

interface ProductRequestFormProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  companyId: Yup.number().required("La empresa es requerida"),
  productId: Yup.number().required("El producto es requerido"),
  reservedDate: Yup.date().required("La fecha de reserva es requerida"),
  reserveDuration: Yup.number()
    .required("La duración de reserva es requerida")
    .min(1, "La duración debe ser mayor que 0"),
  quantity: Yup.number()
    .required("La cantidad es requerida")
    .min(0.01, "La cantidad debe ser mayor que 0"),
  solicitor: Yup.string().required("El solicitante es requerido"),
});

const ProductRequestForm: React.FC<ProductRequestFormProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [totalStock, setTotalStock] = useState<number | null>(null);
  const [stockError, setStockError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [formData, setFormData] = useState<any>({
    companyId: null,
    productId: null,
    reservedDate: dayjs().format("YYYY-MM-DD"),
    reserveDuration: 30,
    quantity: 0,
    solicitor: "",
  });
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getProductRequestById(id!);
      const data = response.data;
      setFormData({
        companyId: data.companyId,
        productId: data.productId,
        reservedDate: data.reservedDate ? dayjs(data.reservedDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
        reserveDuration: data.reserveDuration,
        quantity: data.quantity,
        solicitor: data.solicitor,
      });
      
      // Fetch product object for autocomplete
      if (data.productId) {
        try {
          const productResponse = await getProductById(data.productId);
          setSelectedProduct(productResponse.data);
        } catch (error) {
          console.error("Error fetching product:", error);
        }
      }
      
      // Fetch stock for the product
      if (data.productId && data.companyId) {
        await fetchStock(data.productId, data.companyId);
      }
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar la solicitud de producto", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async (productId: number, companyId: number) => {
    setStockLoading(true);
    setStockError(null);
    try {
      const response = await api.get(`/api/product-requests/stock/${productId}/${companyId}`);
      setTotalStock(response.data.totalStock);
    } catch (error) {
      setStockError("Error al obtener el stock");
      console.error("Error fetching stock:", error);
    } finally {
      setStockLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        ...formData,
        companyId: selectedCompany?.id || null,
      });
      setSelectedProduct(null);
      setLoading(false);
    }
  }, [id, selectedCompany]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        ...values,
        reservedDate: values.reservedDate,
      };

      if (formMode === "edit") {
        await updateProductRequest(id!, payload);
        openSnack("Solicitud de producto actualizada", "success");
      } else {
        await createProductRequest(payload);
        openSnack("Solicitud de producto creada", "success");
      }
      onSubmit();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Error al guardar la solicitud";
      openSnack(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = async (productId: number | null, companyId: number | null) => {
    if (productId && companyId) {
      await fetchStock(productId, companyId);
    } else {
      setTotalStock(null);
      setStockError(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {formMode === "edit" ? "Editar Solicitud de Producto" : "Nueva Solicitud de Producto"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <LGAutocomplete
                  fetchFunction={getProductList}
                  queryParams={{ companyId: values.companyId || selectedCompany?.id || null }}
                  optionLabel={["code", "description"]}
                  optionSeparator=" | "
                  label="Producto"
                  value={selectedProduct}
                  onChange={(event: React.SyntheticEvent, value: any) => {
                    const productId = value ? value.id : null;
                    setSelectedProduct(value);
                    setFieldValue("productId", productId);
                    handleProductChange(productId, values.companyId || selectedCompany?.id || null);
                  }}
                  error={touched.productId && Boolean(errors.productId)}
                  helperText={touched.productId && errors.productId}
                />
              </Grid>

              {values.productId && (values.companyId || selectedCompany?.id) && (
                <Grid item xs={12} md={6}>
                  <Box>
                    {stockLoading ? (
                      <CircularProgress size={20} />
                    ) : stockError ? (
                      <Alert severity="error">{stockError}</Alert>
                    ) : totalStock !== null ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Stock Total Disponible: <strong>{totalStock.toFixed(2)}</strong>
                        </Typography>
                        {values.quantity > 0 && values.quantity > totalStock && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            La cantidad solicitada ({values.quantity}) excede el stock disponible ({totalStock.toFixed(2)})
                          </Alert>
                        )}
                      </Box>
                    ) : null}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FastField
                  name="reservedDate"
                  as={TextField}
                  label="Fecha de Reserva"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={touched.reservedDate && Boolean(errors.reservedDate)}
                  helperText={touched.reservedDate && errors.reservedDate}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FastField
                  name="reserveDuration"
                  as={TextField}
                  label="Duración de Reserva (días)"
                  type="number"
                  fullWidth
                  error={touched.reserveDuration && Boolean(errors.reserveDuration)}
                  helperText={touched.reserveDuration && errors.reserveDuration}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FastField
                  name="quantity"
                  as={TextField}
                  label="Cantidad"
                  type="number"
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                  error={touched.quantity && Boolean(errors.quantity)}
                  helperText={touched.quantity && errors.quantity}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FastField
                  name="solicitor"
                  as={TextField}
                  label="Solicitante"
                  fullWidth
                  error={touched.solicitor && Boolean(errors.solicitor)}
                  helperText={touched.solicitor && errors.solicitor}
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" type="submit" disabled={loading}>
                  {formMode === "edit" ? "Actualizar" : "Crear"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default ProductRequestForm;

